/**
 * Node modules
 */
const _ = require('lodash');

/**
 * Enums
 */
const {
  validAccountPrinterReceiptFormats,
  validAccountTypes,
  validCustomerPropertyTypes,
  validDealCompilers,
  validManipulationModes,
  validSystemPermissionTypes,
} = require('../enums');

/**
 * Constant variables
 */
const accountId = {
  format: 'uuid',
  type: 'string',
};
const accountType = {
  enum: validAccountTypes,
  type: 'string',
};
const address = {
  pattern: '^([0-9A-F]{12})$',
  type: 'string',
};
const alias = { type: 'string' };
const amount = { type: 'number' };
const apiKey = { type: 'string' };
const basePercent = {
  maximum: 100,
  minimum: 0,
  type: 'number',
};
const bearerToken = {
  pattern: '^Bearer [a-zA-Z0-9\\-_]+?\\.[a-zA-Z0-9\\-_]+?\\.([a-zA-Z0-9\\-_]+)?$',
  type: 'string',
};
const uplineId = {
  format: 'uuid',
  type: 'string',
};
const compositeKey = { type: 'string' };
const creditAmount = { type: 'number' };
const customerId = {
  format: 'uuid',
  type: 'string',
};
const customerPropertyType = {
  enum: validCustomerPropertyTypes,
  type: 'string',
};
const customerRunnerTicketAddonId = {
  default: null,
  format: 'uuid',
  type: [
    'null',
    'string',
  ],
};
const customerRunnerTicketHookId = {
  default: null,
  format: 'uuid',
  type: [
    'null',
    'string',
  ],
};
const customerSystemTicketNumberPluginId = {
  format: 'uuid',
  type: [
    'null',
    'string',
  ],
};
const date = {
  format: 'date',
  type: 'string',
};
const dealCompiler = {
  enum: validDealCompilers,
  type: 'string',
};
const dealId = {
  format: 'uuid',
  type: 'string',
};
const drawId = {
  format: 'uuid',
  type: 'string',
};
const drawSystemGameId = {
  format: 'uuid',
  type: 'string',
};
const employerId = {
  format: 'uuid',
  type: 'string',
};
const enUs = { type: 'string' };
const expectedAmount = { type: 'number' };
const hasEmployer = {
  default: true,
  type: 'boolean',
};
const hasSelf = {
  default: true,
  type: 'boolean',
};
const identity = { type: 'string' };
const image = { type: 'string' };
const inactivityDuration = {
  type: 'number',
};
const isActivated = { type: 'boolean' };
const isDemo = { type: 'boolean' };
const isIntentionallyRepeating = {
  default: false,
  type: 'boolean',
};
const isMarkedForAuto = {
  default: false,
  type: 'boolean',
};
const isPartialAllowed = {
  default: true,
  type: 'boolean',
};
const isRemoved = { type: 'boolean' };
const isSelected = { type: 'boolean' };
const layouts = {
  properties: {
    input: {
      properties: {
        entries: {
          items: {
            properties: {
              amount: {
                exclusiveMinimum: 0,
                type: 'number',
              },
              systemGameCategoryId: {
                oneOf: [
                  {
                    pattern: '^\\d+$',
                    type: 'string',
                  },
                  {
                    type: 'number',
                  },
                ],
              },
            },
            required: [
              'amount',
              'systemGameCategoryId',
            ],
            type: 'object',
          },
          minItems: 1,
          type: 'array',
          uniqueItems: true,
        },
        mode: {
          const: 'Input',
          type: 'string',
        },
      },
      required: [
        'entries',
        'mode',
      ],
      type: 'object',
    },
    output: {
      properties: {
        entries: {
          items: {
            properties: {
              amount: {
                exclusiveMinimum: 0,
                type: 'number',
              },
              systemGameCategoryId: {
                oneOf: [
                  {
                    pattern: '^\\d+$',
                    type: 'string',
                  },
                  {
                    type: 'number',
                  },
                ],
              },
            },
            required: [
              'amount',
              'systemGameCategoryId',
            ],
            type: 'object',
          },
          maxItems: 1,
          minItems: 1,
          type: 'array',
          uniqueItems: true,
        },
        mode: {
          const: 'Output',
          type: 'string',
        },
      },
      required: [
        'entries',
        'mode',
      ],
      type: 'object',
    },
  },
  required: [
    'output',
    'input',
  ],
  type: 'object',
};
const limitMode = { type: 'string' };
const locale = { type: 'string' };
const manipulationMode = {
  enum: validManipulationModes,
  type: 'string',
};
const model = {
  pattern: '^\\d+$',
  type: 'string',
};
const multiplier = {
  minimum: 0,
  type: 'number',
};
const n = { oneOf: [{ pattern: '^\\d+$', type: 'string' }, { type: 'number' }] };
const nickname = {
  default: null,
  type: [
    'null',
    'string',
  ],
};
const note = {
  default: null,
  type: [
    'null',
    'string',
  ],
};
const number = { type: 'string' };
const objectId = {
  format: 'uuid',
  type: 'string',
};
const objectIds = {
  items: {
    format: 'uuid',
    type: 'string',
  },
  minItems: 1,
  type: 'array',
  uniqueItems: true,
};
const offsetPercent = {
  maximum: 100,
  minimum: 0,
  type: 'number',
};
const parentRootId = {
  format: 'uuid',
  type: 'string',
};
const password = { type: 'string' };
const percent = {
  maximum: 100,
  minimum: 0,
  type: 'number',
};
const pin = { type: 'string' };
const rawFile = { type: 'object' };
const reason = {
  isNotEmpty: true,
  type: 'string',
};
const receiptFormat = {
  enum: validAccountPrinterReceiptFormats,
  type: 'string',
};
const rootId = {
  format: 'uuid',
  type: 'string',
};
const sourceAddress = { type: 'string' };
const sourceNumber = { type: 'string' };
const systemGameCategoryId = { oneOf: [{ pattern: '^\\d+$', type: 'string' }, { type: 'number' }] };
const systemGameCategoryIds = {
  items: {
    oneOf: [
      {
        pattern: '^\\d+$',
        type: 'string',
      },
      {
        type: 'number',
      },
    ],
  },
  minItems: 1,
  type: 'array',
  uniqueItems: true,
};
const systemGameId = { oneOf: [{ pattern: '^\\d+$', type: 'string' }, { type: 'number' }] };
const systemGamePrizeId = { oneOf: [{ pattern: '^\\d+$', type: 'string' }, { type: 'number' }] };
const systemGameTypeId = { oneOf: [{ pattern: '^\\d+$', type: 'string' }, { type: 'number' }] };
const systemId = {
  format: 'uuid',
  type: 'string',
};
const systemPermissionType = {
  enum: validSystemPermissionTypes,
  type: 'string',
};
const systemTicketNumberPluginId = {
  pattern: '^\\d+$',
  type: 'string',
};
const thTh = { type: 'string' };
const type = { type: 'string' };
const userAgent = { type: 'string' };
const userId = {
  format: 'uuid',
  type: 'string',
};
const username = { type: 'string' };

/**
 * Functions
 */
const createQueryComponent = (scopes, hasSocket = true) => {
  const component = {
    properties: {},
    required: [],
    type: 'object',
  };
  if (!_.isEmpty(scopes)) {
    component.properties.keyset = { type: 'string' };
    component.properties.limit = {
      pattern: '^((?:0*[1-9][0-9]*)|(?:all))$',
      type: 'string',
    };
    component.properties.scope = {
      default: 'DEFAULT',
      enum: scopes,
      type: 'string',
    };
    component.properties.search = { type: 'string' };
    component.properties.sort = { type: 'string' };
    component.required.push('limit');
  }
  if (hasSocket) {
    component.properties.socketId = { type: 'string' };
    component.required.push('socketId');
  }
  return component;
};
module.exports = {
  accountId,
  accountType,
  address,
  alias,
  amount,
  apiKey,
  basePercent,
  bearerToken,
  compositeKey,
  createQueryComponent,
  creditAmount,
  customerId,
  customerPropertyType,
  customerRunnerTicketAddonId,
  customerRunnerTicketHookId,
  customerSystemTicketNumberPluginId,
  date,
  dealCompiler,
  dealId,
  drawId,
  drawSystemGameId,
  employerId,
  enUs,
  expectedAmount,
  hasEmployer,
  hasSelf,
  identity,
  image,
  inactivityDuration,
  isActivated,
  isDemo,
  isIntentionallyRepeating,
  isMarkedForAuto,
  isPartialAllowed,
  isRemoved,
  isSelected,
  layouts,
  limitMode,
  locale,
  manipulationMode,
  model,
  multiplier,
  n,
  nickname,
  note,
  number,
  objectId,
  objectIds,
  offsetPercent,
  parentRootId,
  password,
  percent,
  pin,
  rawFile,
  reason,
  receiptFormat,
  rootId,
  sourceAddress,
  sourceNumber,
  systemGameCategoryId,
  systemGameCategoryIds,
  systemGameId,
  systemGamePrizeId,
  systemGameTypeId,
  systemId,
  systemPermissionType,
  systemTicketNumberPluginId,
  thTh,
  type,
  uplineId,
  userAgent,
  userId,
  username,
};
